/// <reference types="vite/client" />
import {
  GAME,
  FINAL_WAVE,
  resolveSpawnPolicy,
  applyShieldData,
} from "./core/config.ts";
import { registerUITranslations } from "./ui/translations.ts";
import { setLocale, t } from "./utils/i18n.ts";
import { loadGameData } from "./data/DataLoader.ts";
import { applyFirewallData, FIREWALL_CAP } from "./entities/Firewall.ts";
import { DraftUI } from "./ui/DraftUI.ts";
import { SkillOverview } from "./ui/SkillOverview.ts";
import { TowerSelector } from "./ui/TowerSelector.ts";
import { HUD } from "./ui/HUD.ts";
import { MusicDirector } from "./systems/MusicDirector.ts";
import { PreRunScreen } from "./ui/PreRunScreen.ts";
import { GameOverScreen } from "./ui/GameOverScreen.ts";
import { VictoryScreen } from "./ui/VictoryScreen.ts";
import { OperatorUnlockBanner } from "./ui/OperatorUnlockBanner.ts";
import { OPERATOR_NAMES } from "./ui/PreRunScreenData.ts";
import { ActCard } from "./ui/ActCard.ts";
import { SellConfirmDialog } from "./ui/SellConfirmDialog.ts";
import { BreachSystem } from "./systems/BreachSystem.ts";
import { BreachCinematic } from "./ui/BreachCinematic.ts";
import { PayloadRack, MAX_PAYLOADS } from "./systems/PayloadRack.ts";
import { SpawnDetectionSystem } from "./systems/SpawnDetectionSystem.ts";
import { WaveIndicator } from "./ui/WaveIndicator.ts";
import { SpawnAlert } from "./ui/SpawnAlert.ts";
import { EnemyReveal } from "./ui/EnemyReveal.ts";
import { EnemyLibrary } from "./ui/EnemyLibrary.ts";
import { PathRevealAnimation } from "./rendering/PathRevealAnimation.ts";
import { PayloadDetonationFx } from "./rendering/PayloadDetonationFx.ts";
import { SkillDeckBar } from "./ui/SkillDeckBar.ts";
import { PayloadRackBar } from "./ui/PayloadRackBar.ts";
import { applyBoardChrome } from "./ui/BoardChrome.ts";
import { playBoardEntrance } from "./ui/BoardEntrance.ts";
import { LiveBars } from "./ui/LiveBars.ts";
import { GameLoop } from "./engine/GameLoop.ts";
import { PerfGovernor } from "./engine/PerfGovernor.ts";
import { GameBanner } from "./ui/GameBanner.ts";
import { ScannerReadout } from "./ui/ScannerReadout.ts";
import { BootSequence, type BootAudioBridge } from "./ui/BootSequence.ts";
import { PauseOverlay } from "./ui/PauseOverlay.ts";
import { AchievementTracker } from "./systems/AchievementTracker.ts";
import { MetaStateManager } from "./persistence/MetaStateManager.ts";
import type { LoadResult } from "./persistence/MetaStateManager.ts";
import { createStorageAdapter } from "./persistence/createStorageAdapter.ts";
import { HttpLeaderboardClient } from "./persistence/HttpLeaderboardClient.ts";
import { NullLeaderboardClient } from "./persistence/NullLeaderboardClient.ts";
import { LeaderboardService } from "./persistence/LeaderboardService.ts";
import {
  applyModulesToSystems,
  getAutoDegradeThreshold,
} from "./systems/ModuleEffects.ts";
import { registerModules } from "./systems/ModuleRegistry.ts";
import {
  applyOperatorToSystems,
  applyOperatorsData,
} from "./systems/OperatorEffects.ts";
import {
  applyThreatLevelsData,
  applyThreatLevelToSystems,
} from "./systems/ThreatEffects.ts";
import { handleRunEnd } from "./game/runEndHandler.ts";
import { setupEventHandlers } from "./game/eventHandlers.ts";
import { BossArrival } from "./ui/BossArrival.ts";
import { NearLossDirector } from "./ui/NearLossDirector.ts";
import { setupCombatFeel } from "./ui/CombatFeel.ts";
import { FEEL_BEATS, milestoneFlashAlpha } from "./core/feelBudget.ts";
import { PRIORITY as COMMS_PRIORITY } from "./ui/commsContent.ts";
import { setupMomentDirector } from "./ui/MomentDirector.ts";
import { ComboReadout } from "./ui/ComboReadout.ts";
import { FirstRunCoach } from "./ui/FirstRunCoach.ts";
import { isReducedMotion } from "./engine/MotionSettings.ts";
import { ECHELON } from "./rendering/designTokens.ts";
import type { GameCenterBridge } from "./platform/gameCenter.ts";
import { setupInputHandlers } from "./game/inputHandlers.ts";
import type {
  InputHandlerDeps,
  CardEffectHandlerDeps,
} from "./game/handlers/types.ts";
import { applyCardEffect } from "./game/cardEffectHandlers.ts";
import { createRenderPipeline } from "./game/renderPipeline.ts";
import { PHASE_BUILD, PHASE_COMBAT, PHASE_PAUSED } from "./game/types.ts";
import {
  enterPause,
  exitPause,
  setPlacementMode,
  setSelectedTowerType,
  setSelectedTower,
  setTimeWarpAvailable,
  clearPayloadTargeting,
  setWaveCountdown,
  setWaveStartedThisCountdown,
} from "./game/stateIntents.ts";
import { COLORS } from "./core/constants.ts";
import type { PreRunSelection } from "./ui/PreRunScreen.ts";
import type { TowerConfig } from "./entities/types.ts";
import type { GameContext, RuntimeState } from "./game/types.ts";
import type { MetaState } from "./core/MetaState.ts";
import { prefillInitials } from "./core/initials.ts";
import { createGrid } from "./bootstrap/createGrid.ts";
import { createSystems } from "./bootstrap/createSystems.ts";
import { createRenderers } from "./bootstrap/createRenderers.ts";
import { resetForNewRun } from "./bootstrap/resetForNewRun.ts";
import { installErrorBoundary } from "./bootstrap/installErrorBoundary.ts";
import {
  crumb,
  crumbFrames,
  enableDiagnostics,
  nextBootContextSeq,
  showDiagnosticOverlay,
} from "./platform/diagnostics.ts";
import { applyFxLiteIfNeeded } from "./platform/fxLite.ts";
import { createRng, randomSeed } from "./core/Rng.ts";
import { requireElement } from "./utils/dom.ts";
import { displayDpr } from "./utils/displayDpr.ts";
import { isCompactScreen } from "./utils/deviceTier.ts";
import { setUiScale } from "./utils/uiScale.ts";
import { setChromeScale } from "./utils/chromeScale.ts";
import { PerfHud, resolveInitialPerfHudFlag } from "./ui/PerfHud.ts";
import { getStageFit } from "./utils/stage.ts";
import { shouldShowPathInkReview } from "./rendering/PathInk.ts";
import {
  computeUiScaleFloor,
  computeChromeScale,
} from "./utils/stageLayout.ts";
import { ensureA11yStyles } from "./ui/a11y.ts";
import { uiChrome } from "./ui/UIChromeCanvas.ts";
import { UIBloom } from "./ui/UIBloom.ts";
import { ensureUIFont } from "./ui/BootPixelFont.ts";
import { TowerActionController } from "./game/TowerActionController.ts";

function createRuntimeState(): RuntimeState {
  return {
    phase: PHASE_BUILD,
    gold: GAME.INITIAL_CREDITS,
    lives: GAME.INITIAL_HP,
    wave: 0,
    waveActive: false,
    selectedTowerType: null,
    placementMode: "tower",
    towers: [],
    firewalls: [],
    // GA-17: FIREWALL_CAP (3) is authoritative at runtime — resetForNewRun.ts:56
    // unconditionally overwrites this before any run-start path can place a
    // firewall (see its own "called EXACTLY ONCE per run-start" contract). This
    // is a pre-first-run placeholder only; keep it in sync with resetForNewRun,
    // not the other way. D-322 / AC-5 (Task-007): FIREWALL_CAP is data-driven
    // via `firewalls.json` `maxFirewalls` (byte-safe fallback 3).
    maxFirewalls: FIREWALL_CAP,
    pathPixels: [],
    timeWarpAvailable: false,
    timeWarpDuration: 5,
    recursiveLoopThreshold: 0,
    recursiveLoopCounter: 0,
    globalDamageMult: 1,
    resonanceSynergyEnabled: false,
    bossHpMult: 1,
    bossHealingDisabled: false,
    skillsCostZero: false,
    firewallSlowActive: false,
    firewallSlowFactor: 1.0,
    showAllHPBars: false,
    targetingMode: "default",
    previousPhase: null,
    selectedTower: null,
    hoveredTower: null,
    runStats: { enemiesKilled: 0, towersBuilt: 0, goldEarned: 0, goldSpent: 0 },
    waveCountdown: 0,
    waveCountdownMax: 10000,
    waveStartedThisCountdown: false,
    perfectStreak: 0,
    isEndlessMode: false,
    logicBombs: [],
    payloadTargetingMode: null,
    payloadTargetingCard: null,
    maxLives: 20,
    cascadeActive: false,
    cascadeDamage: 0,
    campaignLivesBonus: 0,
  };
}

function resolveSeed(): number {
  let seed = randomSeed();
  if (import.meta.env.DEV) {
    const raw = new URLSearchParams(window.location.search).get("seed");
    if (raw !== null) {
      const parsed = Number.parseInt(raw, 10);
      if (Number.isFinite(parsed)) {
        seed = parsed >>> 0;
      }
    }
  }
  return seed;
}

// ═══════════════════════════════════════════
//  BOOT
// ═══════════════════════════════════════════
async function boot(): Promise<void> {
  // On-device crash diagnostics (native only; DCE'd from the web bundle). Mark
  // every (re)boot so a crash-loop shows as repeated ⟲ boot markers, each
  // preceded by the step where the previous run died. See platform/diagnostics.
  if (__NATIVE_SHELL__) {
    enableDiagnostics();
    crumb("boot:start");
    // S191 Defect A — the load-bearing boot-loop discriminator. The DURABLE
    // context counter bumps once per fresh bundle eval: it CLIMBS across the
    // trail's `boot:start` crumbs when a WKWebView content-process reap restarts
    // the bundle (a native reload — Capacitor's webViewWebContentProcessDidTerminate
    // does bridge.reset() + webView.reload()), but stays PUT if the boot FLOW
    // re-runs inside the same JS context (a logic loop, where only `boot:enter`
    // repeats). Also stamp the resolved dpr + capped backing-store dpr + device
    // tier — the suspected jetsam driver (full-area canvases × dpr) is right here.
    crumb(
      `boot:ctx=${nextBootContextSeq()} dpr=${
        window.devicePixelRatio
      } cap=${displayDpr()} ` +
        `stage=${window.innerWidth}x${window.innerHeight} ${
          isCompactScreen() ? "compact" : "tablet"
        }`
    );
    // Mitigate the dpr-3 PreRun render-process crash BEFORE any screen paints.
    // (After enableDiagnostics so it can crumb whether FX-lite engaged + the dpr.)
    applyFxLiteIfNeeded();
    // Surface JS errors INTO the trail too, so a build-time throw shows as an
    // ERROR line — distinguishing it from a silent native/GPU-process crash
    // (where the trail simply stops after a clean mount).
    window.addEventListener("error", (e) =>
      crumb(`ERROR ${e.message ?? e.error}`)
    );
    window.addEventListener("unhandledrejection", (e) =>
      crumb(`REJECT ${String(e.reason)}`)
    );
    // S191 Defect A — WKWebView page lifecycle. A content-process reap is ABRUPT:
    // it leaves NO `life:pagehide` before the next context's `boot:start` (the
    // page was killed, not navigated). A graceful bfcache/navigation reload fires
    // pagehide→pageshow; an OS background fires only visibilitychange. So a fresh
    // `boot:start` (climbing ctx) with no preceding pagehide is the jetsam-kill
    // signature; pagehide/visibilitychange pairs are benign background churn.
    window.addEventListener("pagehide", () => crumb("life:pagehide"));
    window.addEventListener("pageshow", (e) =>
      crumb(`life:pageshow persisted=${e.persisted}`)
    );
    document.addEventListener("visibilitychange", () =>
      crumb(`life:visibility=${document.visibilityState}`)
    );
  }
  registerUITranslations();
  // BL-37 — VT323 DOM-ready early (no Courier flash) + mount the one
  // full-viewport engine-material UI-chrome canvas behind the DOM panels.
  void ensureUIFont();
  uiChrome.mount();
  const data = await loadGameData("./data");
  applyFirewallData(data.firewalls);
  // D-322 / AC-5 (Task-007) — the optional shield tuning override
  // (`waves.json` `shield`); absent → the config.ts byte-safe default.
  applyShieldData(data.waves.shield);
  // D-322 / AC-5 (Task-007) — the optional operators override
  // (`operators.json`); absent → the byte-safe OperatorEffects defaults.
  applyOperatorsData(data.operators);
  // Chain step A (2026-08-18): the threat-profile override — data first, code
  // fallback byte-identical (same D-322 pattern as the operators line above).
  applyThreatLevelsData(data.threatLevels);
  // BL-90: modules are data-driven — inject the loaded modules.json into the
  // registry before any consumer (PreRun grid, run start, unlock path) reads it.
  registerModules(data.modules);

  // ─── Locale (BL-115, the i18n keystone) ────
  // Load the persisted meta state and seed the runtime locale BEFORE any UI is
  // constructed. The HUD gear menu, the PreRun screen, etc. resolve their `t()`
  // strings at *construction* time, so the locale must be set first — a
  // persisted `"de"` then lights the whole game German at boot. The storage
  // adapter degrades to in-memory when storage is blocked (BL-85), so this can
  // never throw the boot. Determinism: the locale is render-only (sim-blind).
  const storageAdapter = createStorageAdapter();
  const metaStateManager = new MetaStateManager(storageAdapter);
  let metaState: MetaState;
  let metaLoadStatus: LoadResult["status"] = "clean";
  try {
    const loadResult = await metaStateManager.load();
    metaState = loadResult.state;
    metaLoadStatus = loadResult.status;
  } catch {
    metaState = metaStateManager.getDefault();
  }
  setLocale(metaState.settings.language);

  // ─── Grid + Path ───────────────────────────
  const gs = createRuntimeState();
  const sessionSeed = resolveSeed();
  const rng = createRng(sessionSeed);

  // BL-145: resolve the spawn placement policy ONCE (data override → typed
  // default). Threaded into both spawn channels so the static layout
  // (createGrid) and the dynamic breach ladder/cap (BreachSystem) read one
  // source. No override ships → DEFAULT_SPAWN_POLICY → byte-identical.
  const spawnPolicy = resolveSpawnPolicy(data.spawn);

  const gridSetup = createGrid(gs, rng, spawnPolicy);
  const { grid, pathfinding, pathRegistry } = gridSetup;

  // ─── Systems ───────────────────────────────
  const systems = await createSystems(gs, gs.pathPixels, data, rng);
  const {
    eventBus,
    rng: systemRng,
    breachRng,
    projectilePool,
    economy,
    waveManager,
    combatSystem,
    synergySystem,
    draftSystem,
    scoreSystem,
    audioEngine,
  } = systems;

  // Task-001 (AC-1, 2026-audio-module-audit) — mirror the dev/diagnostics
  // audio-unlock trace (context state, gesture arm/disarm, resume attempts +
  // outcomes, music/ambience play/defer/flush, decode fail/retry, native-
  // bridge lifecycle) into the on-device crumb trail (platform/diagnostics.ts
  // pattern), so the owner-assisted iOS checklist can read WHY music did or
  // didn't start without a cable/Web Inspector. Native-only; the web build
  // DCEs this __NATIVE_SHELL__ branch. Backfill first (createSystems already
  // ran loadManifest + armGestureResume, so a few early events predate this
  // wiring), then mirror every further event live.
  if (__NATIVE_SHELL__) {
    for (const e of audioEngine.getAudioTrace()) {
      crumb(`audio:${e.kind} ${e.detail}`);
    }
    audioEngine.onAudioTrace((e) => crumb(`audio:${e.kind} ${e.detail}`));
  }

  // Wire PathRegistry into WaveManager (LD-22-12)
  waveManager.setPathRegistry(pathRegistry);

  // ─── Renderers ─────────────────────────────
  const renderers = createRenderers(grid);
  const {
    canvas,
    ctx2d,
    webglEffects,
    gridRenderer,
    entityRenderer,
    firewallRenderer,
    hazardPoolRenderer,
    particleSystem,
    screenEffects,
    floatingDmg,
  } = renderers;

  // BL-44 — the ONE perf load brain. Reads render-side signals only (never sim)
  // and drives the arena-only degrade ladder. The arena WebGL2Effects.setQuality
  // (chroma/bloom rungs) + the ParticleSystem soft cap (rung 2) are the actuators
  // it pushes; motion / density-recede / hit-stop suppression are pulled by their
  // arena consumers. The HUD UIBloom instance is NOT wired here — never degraded.
  const perfGovernor = new PerfGovernor({
    setPostFxQuality: (q) => webglEffects.setQuality(q),
    getPostFxQuality: () => webglEffects.getQuality(),
    setParticleCap: (cap) => particleSystem.setSoftCap(cap),
  });

  // F15 Teil D — the on-device performance read-out. Default OFF; `?perf=1`,
  // `localStorage.aiw_perfhud`, or seven taps on the boot version line turn it
  // on (the last is the only switch reachable in the native iOS shell). It reads
  // render-side signals only and mounts OUTSIDE the scaled stage — see
  // `ui/PerfHud.ts` for why the owner needs frame interval, degrade rung and
  // effective DPR side by side before the iOS-performance question can be
  // answered at all.
  const perfHud = new PerfHud();
  perfHud.attachGovernor(() => perfGovernor.debug());
  perfHud.installSecretToggle(requireElement("game-container"));
  if (resolveInitialPerfHudFlag()) perfHud.setEnabled(true);

  // BL-36: a killed enemy plays a render-only death-pop (the additive burst is
  // emitKill in combatHandlers). Wired here — the enemy is pool-recycled the
  // same frame, so the renderer captures its silhouette params on this event.
  eventBus.on("enemy.killed", ({ enemy }) => {
    entityRenderer.spawnDeathPop(enemy);
  });

  // Chain grid mutation callback: invalidate pathfinding cache + renderer tile cache + registry
  grid.setOnCellChanged(() => {
    pathfinding.invalidateCache();
    pathRegistry.recalculateAll();
    gs.pathPixels = pathRegistry.getPixelPath("primary") ?? [];
    gridRenderer.invalidateCellCaches();
  });

  // ─── UI Layer 4 ────────────────────────────
  const towerData = data.towers as unknown as Record<string, TowerConfig>;
  // BL-212 (S189) — first-run micro-coach (assigned below, after #ui-layer
  // exists). Referenced here so a tower selection advances it to step ②.
  let firstRunCoach: FirstRunCoach | null = null;
  const towerSelector = new TowerSelector(towerData, (type) => {
    if (type === "firewall") {
      setPlacementMode(gs, "firewall");
    } else if (type === null) {
      // Explicit deselect — back to "nothing selected" (no ghost, no build).
      setPlacementMode(gs, "tower");
      setSelectedTowerType(gs, null);
    } else {
      setPlacementMode(gs, "tower");
      setSelectedTowerType(gs, type);
    }
    if (type !== null) firstRunCoach?.advanceToPlace();
  });
  const uiLayer = requireElement("ui-layer");
  firstRunCoach = new FirstRunCoach(uiLayer);
  const hudRow = requireElement("hud-row");
  const skillRow = requireElement("skill-row");
  // BL-62 — the commentary TV now docks in-flow at the base of the LEFT column
  // (#comms-dock), not as a floating #ui-layer overlay (the signed composition).
  const commsDock = requireElement("comms-dock");
  // BL-140 — the LEFT-column dock the new left HUD (Credits · Integrity · Wave)
  // mounts into. SYS.MONITOR (the old left "bash status") is gone; the left HUD
  // and the top HUD (Score + Mult) now read as one symmetric, premium frame.
  // (The enemy-reveal stage still overlays #status-row inset:0 on a first
  // sighting; the left HUD sits beneath it.)
  const statusRow = requireElement("status-row");
  const hud = new HUD(
    hudRow,
    gs,
    eventBus,
    data.waves.templates.length,
    commsDock,
    statusRow,
    { sfxMuted: false, musicMuted: false, sfxVolume: 0.9, musicVolume: 0.6 },
    // Task-013 (AC-17) — the TV pop-out beat hook. __NATIVE_SHELL__ is the
    // usual build-time literal: false for the web build, so this whole branch
    // (and the dynamic import of nativeShell.ts, which is the only module that
    // imports @capacitor/haptics) is dead-code-eliminated — zero bytes, and a
    // true no-op, on web/desktop.
    __NATIVE_SHELL__
      ? (beat) => {
          void import("./platform/nativeShell.ts").then((shell) =>
            shell.triggerPopOutHaptic(beat)
          );
        }
      : undefined,
    // Spec 2026-pause-settings-overlays (AC-4) — the ⚙ now opens a CENTERED
    // settings modal, so it mounts into the same full-stage `#ui-layer` the
    // pause modal uses, not into the thin `#hud-row` strip the HUD bar lives in.
    uiLayer
  );
  // BL-63 — feed the multiplier-transparency cell live combo state (a callback,
  // so the HUD never imports a systems module). All reads are render-only.
  hud.attachMultiplier(() => ({
    multiplier: scoreSystem.comboMultiplier,
    combo: scoreSystem.combo,
    windowRemaining: scoreSystem.comboWindowRemaining(),
    timeoutMult: scoreSystem.comboTimeoutMultiplier,
    scoreMult: scoreSystem.scoreMultiplier,
  }));
  // BL-209 (S189) — combo made VISIBLE ON THE BOARD: a screen-edge aura + a
  // pulsing "×N" that scale with the live multiplier and fade with the combo
  // window. Render-only (reads scoreSystem; never mutates sim) and
  // reduced-motion safe; complements the HUD's multiplier number.
  const comboReadout = new ComboReadout(uiLayer, scoreSystem);
  // BL-215 fix — run the board overlay (its rAF + edge-aura layer) ONLY during a
  // run, never from boot. comboReadout.start() now lives in startRun(); stopping
  // it on return-to-menu drops it out of compositing, so boot / start / pre-run
  // (the iPhone first-launch jetsam-reload window) stay overlay-free.
  eventBus.on("game.returnToMenu", () => comboReadout.stop());
  const waveIndicator = new WaveIndicator(uiLayer);
  const spawnAlert = new SpawnAlert(uiLayer);
  const pathRevealAnimation = new PathRevealAnimation();
  const spawnDetectionSystem = new SpawnDetectionSystem(grid, eventBus);
  gridRenderer.setHiddenSpawnPositionSource(() =>
    spawnDetectionSystem.getHiddenPositions()
  );
  const gameOverScreen = new GameOverScreen(uiLayer);
  const victoryScreen = new VictoryScreen(uiLayer);
  // WOW Pass 2 (item 2) — the celebratory beat when a new AI model comes online
  // at run-end. Read-only: main.ts diffs the unlocked-operator set across the
  // run (the unlock itself stays in runEndHandler) and feeds the name(s) here.
  const operatorUnlockBanner = new OperatorUnlockBanner(uiLayer);
  // S130 (Story Bible §6.4): the act-door transition cards — input-transparent,
  // self-dismissing, render-only (rides the existing wave.started event).
  // WOW Pass 2 (item 3) — the act-arrival ESCALATION sweep. ActCard already
  // shows the door card; the BOSS edge already reads as an event (vignette +
  // flash + banner + sting, uiHandlers/AudioEngine), but crossing into a new Act
  // was card-only. Layer her identity-colour screen sweep on the act edge
  // (magenta Act II → violet Act III). Render-only: the flash auto-damps to 0
  // and the sweep is skipped under reduced motion (calm card remains).
  const actCard = new ActCard(uiLayer, (act) => {
    const color = act === 3 ? ECHELON.DEEP : ECHELON.SIGNAL;
    if (!isReducedMotion()) screenEffects.vignette(color, 1400);
    screenEffects.flash(color, 0.22);
  });
  actCard.attach(eventBus);
  const sellDialog = new SellConfirmDialog(uiLayer);
  const draftUI = new DraftUI(eventBus);
  const skillOverview = new SkillOverview(
    data.cards.cards,
    {
      quipPicker: (card) => hud.narrateCardReaction(card),
    },
    eventBus
  );

  // BL-148: the breach roll draws from its OWN isolated stream (was
  // `() => systemRng.next()`, the shared combat stream — S149 §3). The
  // shared stream no longer advances on the roll, so combat/draft/crit
  // ordering is decoupled from breach randomness.
  const breachSystem = new BreachSystem(
    grid,
    pathfinding,
    breachRng,
    spawnPolicy
  );
  breachSystem.setPathRegistry(pathRegistry);
  breachSystem.init(gridSetup.getSpawnLayout());
  breachSystem.setPrimaryPathLength(gridSetup.primaryPathCellCount);
  const recalcAndSyncBreach = (): void => {
    gridSetup.recalculatePath();
    breachSystem.setPrimaryPathLength(gridSetup.primaryPathCellCount);
    eventBus.emit("path.recalculated");
  };
  spawnDetectionSystem.init(gridSetup.getSpawnLayout());
  const breachCinematic = new BreachCinematic(eventBus);
  // D-333 (owner decision) — "// LOADOUT" moves to the LEFT rail, mounted
  // INSIDE #status-row (a sibling of the left HUD, `hud.attachMultiplier`'s
  // `this.leftContainer` above) rather than a third #left-panel flex child —
  // load-bearing so the EnemyReveal overlay (appended to #status-row below,
  // `position:absolute; inset:0; z-index:5`) keeps covering it. "// PAYLOAD"
  // (PayloadRackBar, below) stays in the right rail (`skillRow`).
  const skillDeckBar = new SkillDeckBar(statusRow, () => {
    skillOverview.show([...draftSystem.cards], gs.wave, null);
  });
  // BL-48 — the ⭐ enemy-reveal stage owns the left panel's headline space as a
  // transient overlay (inset:0 over #status-row, above the left HUD). Fires on a
  // new type's first sighting (the same signal the comms use). The brief board
  // freeze reuses GameLoop.hitStop (loop-delay only → determinism-neutral);
  // hitStopTier self-guards reduced-motion.
  const enemyReveal = new EnemyReveal(statusRow, {
    boardHitStop: () => gameLoop.hitStopTier("short"),
  });
  enemyReveal.attach(eventBus, data.enemies);
  // Task-012 (AC-16, O-11b) — the in-game "THREAT ARCHIVE" overlay (option B,
  // `phase2-options.md` §3): reuses the SAME `TradingCard`/`EnemyHolo` modules
  // the reveal above already ships, reads discovery straight off
  // `enemyReveal.isDiscovered` (no second tracking set), and mounts into
  // `#game-container` like every other full-screen overlay (`SkillOverview`'s
  // pattern). Built lazily on first `show()` — this constructor call is cheap
  // (no card/holo work happens until the gear row opens it).
  const enemyLibrary = new EnemyLibrary(
    document.getElementById("game-container") ?? document.body,
    {
      enemies: data.enemies,
      isDiscovered: (name) => enemyReveal.isDiscovered(name),
      // D-328 — a light haptic tick when the card hero opens. __NATIVE_SHELL__
      // DCEs this whole branch (and the dynamic nativeShell.ts import, the
      // only module that pulls in @capacitor/haptics) off the web bundle.
      onHeroOpenHaptic: __NATIVE_SHELL__
        ? () => {
            void import("./platform/nativeShell.ts").then((shell) =>
              shell.triggerLibraryHeroHaptic()
            );
          }
        : undefined,
    }
  );
  hud.setOnOpenLibrary(() => enemyLibrary.show());
  // S195/BL-282 — the boss-arrival NAME-SLAM: the identity channel of the one
  // arrival moment (MomentDirector's screen-beat + FENRIR's line fire off the
  // same boss.spawned). D-331 (2026-07-31): the identity reveal now lands on
  // the commentary TV, not an in-grid strip — BossArrival only resolves the
  // real-boss guard + the localized name, then hands it to the TV via HUD.
  const bossArrival = new BossArrival();
  bossArrival.attach(eventBus, data.enemies, (title, name) =>
    hud.announceBossArrival(title, name)
  );

  // BL-37 — relight the live side-panel frames on the engine-material chrome
  // canvas (the HUD bar registers itself). These are the core HUD frames; the
  // dialog/overlay floats above the arena and uses the sanctioned CSS lit-edge
  // degrade rung instead (canvas-behind alignment is too fragile there).
  uiChrome.register(requireElement("left-panel"));
  uiChrome.register(requireElement("right-panel"));
  uiChrome.scheduleRelayout();
  // BL-62 — the ONE shared board chrome: a board-wide lit-CRT scanline across
  // the side columns + the premium gold corner-etch on the left dossier zone,
  // so the whole frame reads as one harmonious picture (a single source — no
  // per-component duplication). Static ⇒ cheap + reduced-motion-safe; the BL-44
  // arena budget is untouched (the HUD is not perf-governed).
  applyBoardChrome(statusRow, { corners: ["tl", "tr"] });
  applyBoardChrome(requireElement("right-panel"), { corners: [] });
  // BL-37b / S87 — the MAX UI bar: promote the S86 engine-material chrome to a
  // 2nd instance of the arena's WebGL bloom pipeline so the HUD lights with the
  // same material as the battlefield. Dirty-redraw (runs only when the chrome
  // changes); falls back to the raw S86 chrome if WebGL is unavailable. The
  // arena's own bloom pass (createRenderers → webglEffects) is untouched.
  // BL-44 drop-first seam: under load, disable this BEFORE uiChrome.setDegraded
  // (UI bloom → off → still-lit chrome → flat lit edge), per SYSTEMS_MAP §4.
  //
  // S196 — the chrome bloom is the SECOND of the two WebGL2 contexts that
  // `displayDpr`'s comment names as the jetsam driver, and it is created HERE,
  // eagerly inside boot(), BEFORE the boot screen paints (showBootSequence runs
  // at the tail of boot()). On the native PHONE shell — the tight WKWebView
  // jetsam budget where the dpr-1 cap already proved insufficient, and the exact
  // surface that reload-loops to the boot screen — skip it entirely:
  // `setPostFx(null)` is the sanctioned graceful path (the chrome renders as raw
  // lit S86 material), so the app runs one full WebGL2 context + its framebuffers
  // lighter for its whole lifetime. iPad/desktop (headroom, per the same comment)
  // and every web build keep the premium HUD bloom untouched — the
  // `__NATIVE_SHELL__` build literal DCEs this branch out of the web bundle, so
  // web behaviour + tests are byte-identical.
  if (__NATIVE_SHELL__ && isCompactScreen()) {
    crumb("chrome-bloom:skipped (compact-native)");
  } else {
    const uiBloom = new UIBloom();
    uiBloom.setDpr(displayDpr());
    uiChrome.setPostFx(uiBloom);
  }
  const pauseOverlay = new PauseOverlay(
    uiLayer,
    () => {
      // Bug 14: mouse resume — mirror the ESC resume path exactly.
      if (gs.phase !== PHASE_PAUSED) return;
      // BL-216: this tap is a trusted user gesture — the surest moment to revive a
      // context iOS left suspended after a background trip, so resume audio here.
      audioEngine.resumeContext();
      exitPause(gs);
      eventBus.emit("game.resumed");
    },
    {
      // Spec 2026-pause-settings-overlays (AC-3) — RESTART RUN and EXIT TO MENU
      // moved out of the gear dropdown and onto the pause modal. They emit the
      // identical events the gear rows emitted (via the now-removed
      // `hud.setOnRestart` / `hud.setOnExit` seams), and still no confirmation
      // step.
      onRestart: () => eventBus.emit("game.restart"),
      onExit: () => eventBus.emit("game.returnToMenu"),
      // Spec AC-16 (owner decision OQ-2 = b) — the ⚙ SWAPS to the settings
      // modal. It does not open a second surface: `HUD.openSettings` takes this
      // one down first, and sends `ZURÜCK`/Escape back to it, because it is up
      // right now. The run never leaves PHASE_PAUSED across the trip.
      onOpenSettings: () => hud.openSettings(),
    }
  );
  eventBus.on("game.paused", () => {
    // BL-299 (owner, build 98) — the tower panel sits at z-index 9999, above the
    // pause modal, so an OS-triggered auto-pause (app switch) left it floating
    // over the PAUSIERT dialog and its buttons. A paused run has no tower
    // interaction to offer: the panel closes with the pause, every time.
    towerSelector.hideTowerInfo();
    pauseOverlay.show();
    hud.syncPauseState(true);
  });
  eventBus.on("game.resumed", () => {
    pauseOverlay.hide();
    hud.syncPauseState(false);
  });
  // AC-16 — the other half of the swap: the HUD can only send `ZURÜCK` back to
  // a pause modal it can see. Same injection seam as `hud.setOnOpenLibrary`.
  hud.setPauseSurface(pauseOverlay);

  // ─── BL-287 · TEMPORARY path-colour A/B (delete with the verdict) ─────────
  // The owner settles the conduit green on a real iPhone, in a running game.
  // A query parameter cannot reach a Capacitor build (no address bar), so the
  // switch is on screen — but ONLY where it belongs: always in the native shell,
  // and on web solely behind `?review=1`, because merging to `main` auto-deploys
  // the web build to the public site. Dynamically imported so the size-gated
  // startup chunk does not carry a review instrument.
  if (shouldShowPathInkReview(__NATIVE_SHELL__)) {
    void import("./ui/PathInkReviewChip.ts").then((m) => {
      m.mountPathInkReviewChip(uiLayer, __NATIVE_SHELL__);
    });
  }

  // ─── Native iPad shell (Capacitor) ─────────
  // __NATIVE_SHELL__ is a build-time literal (vite.config define): false for the
  // web build, true only for `vite build --mode capacitor`. The web build thus
  // dead-code-eliminates this block AND the dynamic import, so @capacitor/* adds
  // ZERO bytes to the size-gated bundle. See src/platform/nativeShell.ts.
  if (__NATIVE_SHELL__) {
    void import("./platform/nativeShell.ts").then((shell) => {
      shell.initNativeShell({
        bus: eventBus,
        pauseForBackground: () => {
          if (gs.phase === PHASE_BUILD || gs.phase === PHASE_COMBAT) {
            enterPause(gs);
            eventBus.emit("game.paused");
          }
        },
        // P1-2 / BL-216: restore the Web-Audio context on foreground (iOS may
        // leave it suspended after backgrounding). Try an immediate resume, AND
        // re-arm the gesture listeners so that if that resume isn't honoured
        // (foreground isn't always a "trusted" gesture), the player's next tap
        // re-resumes audio. Idempotent; the pause overlay still gates the actual
        // run resume.
        resumeFromForeground: () => {
          audioEngine.resumeContext();
          audioEngine.armGestureResume();
        },
      });
    });
  }

  const preRunScreen = new PreRunScreen(uiLayer, eventBus);

  const triggerWaveStart = (): void => {
    if (!gs.waveActive && gs.phase === PHASE_BUILD) {
      eventBus.emit("wave.manualStart");
      waveManager.startWave();
    }
  };
  waveIndicator.show(gs.wave + 1, triggerWaveStart);
  skillDeckBar.update([...draftSystem.cards]);

  // ─── Payload Rack (PT-14) ──────────────────
  const payloadRack = new PayloadRack(eventBus);
  draftSystem.setPayloadRack(payloadRack);
  const payloadRackBar = new PayloadRackBar(skillRow, payloadRack, eventBus);
  // BL-56b — the two live Pro-Bars (wave-progress + payload-charge), wired to
  // the REAL ProBar via O(1) render-only accessors (the bars BL-56 only demoed
  // in docs/hud_probar.html). Determinism-neutral: pure reads, refreshed on the
  // already-emitted wave/payload events (no per-frame poll, no sim write).
  const liveBars = new LiveBars(
    skillRow,
    {
      waveProgress: () => ({
        cleared: waveManager.wavesCleared,
        total: FINAL_WAVE,
      }),
      payloadCharge: () => ({ count: payloadRack.count, max: MAX_PAYLOADS }),
    },
    eventBus
  );
  // Render-only detonation spectacle (determinism-safe) — drawn from the
  // render pipeline so its shockwave is bloom-lit in world space.
  const payloadDetonationFx = new PayloadDetonationFx(
    eventBus,
    screenEffects,
    particleSystem,
    audioEngine
  );

  // ─── Game Context ──────────────────────────
  const gameCtx: GameContext = {
    gs,
    eventBus,
    grid,
    pathfinding,
    pathRegistry,
    projectilePool,
    economy,
    waveManager,
    combatSystem,
    synergySystem,
    draftSystem,
    scoreSystem,
    payloadRack,
    entityRenderer,
    gridRenderer,
    particleSystem,
    screenEffects,
    draftUI,
    skillOverview,
    towerSelector,
    hud,
    waveIndicator,
    liveBars,
    enemyReveal,
    skillDeckBar,
    gameOverScreen,
    victoryScreen,
    sellDialog,
    breachSystem,
    breachCinematic,
    webglEffects,
    perfGovernor,
    towerData,
    canvas,
    ctx: ctx2d,
    recalculatePath: recalcAndSyncBreach,
    failsafeAvailable: false,
    firewallRenderer,
    hazardPoolRenderer,
    gameBanner: new GameBanner(),
    // D-331 — the Intrusion Scanner's own reading surface, and the generic
    // grid-banner→TV landing seam (STORY/NARRATION/PRAISE/STATUS text).
    scannerReadout: new ScannerReadout(uiLayer),
    commsRouter: {
      status: (text: string, priority?: number) =>
        hud.announceStatus(text, priority),
    },
    floatingDmg,
    spawnAlert,
    pathRevealAnimation,
    payloadDetonationFx,
    spawnDetectionSystem,
    moduleBaselines: {
      rangeMultiplier: 1,
      bossDamageReduction: 1,
      globalDamageMult: 1,
    },
    rng: systemRng,
  };

  // ─── Wire Systems ──────────────────────────
  const gameLoop = new GameLoop(
    () => {},
    () => {}
  );
  gameLoop.setIsPaused(() => gs.phase === PHASE_PAUSED);
  payloadDetonationFx.setGameLoop(gameLoop);

  // BL-87: global error boundary / app-shell net. Catches uncaught errors and
  // unhandled promise rejections (async submitScore/save, EventBus-callback
  // throws, post-boot throws outside the loop) that the per-frame GameLoop net
  // can't see — logs a PII-free record + shows a recoverable in-game notice,
  // keeping the app alive instead of dying silently to devtools.
  installErrorBoundary({
    notify: (msg) => gameCtx.gameBanner.show(msg, COLORS.AMBER_WARN, 3000),
    buildId: __APP_VERSION__,
  });

  const disposeHandlers = setupEventHandlers(gameCtx, gameLoop);
  // BL-208 (S189) — kill-juice loop (render/UI only). Registered LAST so its
  // score.changed listener captures the kill's awarded delta (emitted
  // synchronously inside the enemy.killed chain) before its own enemy.killed
  // listener spawns the on-board "+N" pop / punch / tick for that same kill.
  const disposeCombatFeel = setupCombatFeel({
    eventBus,
    screenEffects,
    floatingDmg,
    gameLoop,
    audio: audioEngine,
  });
  // BL-237 (WOW Pass 3) — hero-moment beats (boss arrival/defeat, GOLD reached,
  // mainframe critical). The visual-impact sibling of CombatFeel; render/feel
  // only, sim byte-identical. Audio (AudioEngine) + haptics (nativeShell) are
  // already mapped off the same events, so this only adds the arena visuals.
  const disposeMomentDirector = setupMomentDirector({
    eventBus,
    screenEffects,
    gameLoop,
    particles: particleSystem,
  });
  gameCtx.disposeHandlers = () => {
    disposeHandlers();
    disposeCombatFeel();
    disposeMomentDirector();
  };

  // Wire payload rack bar → effect context (so deploy calls applyCardEffect)
  payloadRackBar.setEffectContext(gameCtx, gs);

  // Cheap "enemies present?" boolean for rack hot pulse (throttled via events)
  const updateRackHot = (): void => {
    const hasActive = waveManager.enemies.some((e) => e.active);
    payloadRackBar.setHot(hasActive && gs.waveActive);
  };
  eventBus.on("enemy.spawned", updateRackHot);
  eventBus.on("enemy.killed", updateRackHot);
  eventBus.on("enemy.reachedEnd", updateRackHot);
  eventBus.on("wave.completed", () => payloadRackBar.setHot(false));
  eventBus.on("game.timeWarpChanged", (available: unknown) => {
    payloadRackBar.setTimeWarpAvailable(available as boolean);
  });
  eventBus.on("game.activateTimeWarp", () => {
    if (gs.timeWarpAvailable && gs.waveActive) {
      setTimeWarpAvailable(gs, false);
      // GA-40: gs.timeWarpDuration is SECONDS (stateIntents.setTimeWarpDuration);
      // activateTimeWarp takes MILLISECONDS. Passing it raw gave a 5 ms freeze.
      combatSystem.activateTimeWarp(gs.timeWarpDuration * 1000);
      eventBus.emit("game.timeWarpChanged", false);
    }
  });

  const towerActions = new TowerActionController({
    gs,
    economy,
    grid,
    synergySystem,
    waveManager,
    sellDialog,
    eventBus,
    recalculatePath: recalcAndSyncBreach,
    pathRegistry,
  });

  // Construct narrow input handler deps.
  // BUG-1: `gs` MUST be the LIVE runtime-state object, not a per-field snapshot.
  // A previous "narrow deps" refactor copied each primitive (selectedTowerType,
  // phase, lives, …) by value once at boot, so the tower selector's later writes
  // never reached placement — every tower built as "fire"/MALWARE, and tower
  // selection/upgrade silently broke. The narrowing belongs in the *type*
  // (`InputHandlerDeps.gs` is a `Pick<RuntimeState, …>`); back it with the live
  // object so selection → ghost → placement observe one source of truth.
  const inputHandlerDeps: InputHandlerDeps = {
    eventBus: gameCtx.eventBus,
    gs: gameCtx.gs,
    canvas: gameCtx.canvas,
    grid: gameCtx.grid,
    pathfinding: gameCtx.pathfinding,
    economy: gameCtx.economy,
    towerData: gameCtx.towerData,
    waveManager: gameCtx.waveManager,
    synergySystem: gameCtx.synergySystem,
    combatSystem: gameCtx.combatSystem,
    towerSelector: gameCtx.towerSelector,
    skillOverview: gameCtx.skillOverview,
    draftSystem: gameCtx.draftSystem,
    hud: gameCtx.hud,
    sellDialog: gameCtx.sellDialog,
    recalculatePath: recalcAndSyncBreach,
    pathRegistry: gameCtx.pathRegistry,
    spawnDetectionSystem: gameCtx.spawnDetectionSystem,
    gameBanner: gameCtx.gameBanner,
    enemyLibrary,
    deployTargetedPayload: (target) => {
      const card = gs.payloadTargetingCard;
      if (!card) return;
      // Find and deploy the card from the rack
      const idx = payloadRack.payloads.findIndex((c) => c === card);
      if (idx === -1) return;
      const deployed = payloadRack.deploy(idx);
      if (!deployed) return;
      // Clear targeting state
      clearPayloadTargeting(gs);
      // Apply with target context
      const ctx = gameCtx as unknown as CardEffectHandlerDeps;
      applyCardEffect(deployed, { ...ctx, payloadTarget: target });
    },
  };
  const mouse = setupInputHandlers(inputHandlerDeps);
  const { update, render, corruption } = createRenderPipeline(
    gameCtx,
    mouse,
    towerActions
  );
  gameLoop.setCallbacks((dt) => {
    update(dt);
    // S195/BL-283 — the near-loss director's O(1) lives poll rides the same
    // tick (band edges only touch the channels).
    nearLoss.update();
  }, render);

  // P22.1/P22.2 DEBUG: expose runtime for live introspection. Gated behind
  // import.meta.env.DEV so production builds never leak `window.__game`.
  // Hot path: `window.__game.pathRegistry.getAllPathEntries()` etc.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__game = Object.assign(gameCtx, {
      // Task-001 (AC-1, 2026-audio-module-audit) — dev-only, read-only
      // audio-unlock trace surface (ctx state, gesture arm/disarm, resume
      // attempts + outcomes, music/ambience play/defer/flush, decode fail/
      // retry, native-bridge lifecycle). Feeds the Playwright harness (AC-2)
      // and manual dev repro; the native crumb mirror (above) covers the
      // device leg. `Object.assign` mutates + returns the SAME `gameCtx`
      // reference (not a copy), so every other `window.__game.*` hot path
      // stays live.
      audioTrace: () => audioEngine.getAudioTrace(),
      // Task-002 (AC-2, 2026-audio-module-audit) — dev-only, read-only music
      // playback status, alongside the trace above. The trace records EVENTS
      // (play/defer/flush) but not a live-queryable "is it audible right now"
      // state; the Playwright harness asserts on this directly instead of
      // inferring it from the last trace entry.
      isMusicPlaying: () => audioEngine.isMusicPlaying(),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__dumpState = () => ({
      seed: systemRng.seed,
      wave: gs.wave,
      phase: gs.phase,
      gold: gs.gold,
      lives: gs.lives,
      towers: {
        count: gs.towers.length,
        items: gs.towers.map((t) => ({
          x: t.x,
          y: t.y,
          element: t.element,
          level: t.level,
        })),
      },
      synergies: synergySystem.activeSynergies.map((s) => ({
        id: s.id,
        name: s.name,
        effectType: s.effect.type,
        towerCount: s.towers.length,
      })),
      activeCards: draftSystem.cards.map((c) => ({
        id: c.id,
        name: c.name,
        effectType: c.effect.type,
        target: c.effect.target,
      })),
      breach: breachSystem.getDebugState(),
    });
    console.info(
      "[dev] window.__dumpState() for a state snapshot; run seed=<n>"
    );
  }

  // ─── UI Event Wiring ───────────────────────
  // BL-212 (S189) — the first-run coach clears on the FIRST successful
  // placement (a tower OR a firewall — both end the "tap to place" step), and
  // tutorialSeen is persisted so it never shows again.
  const dismissFirstRunCoach = (): void => {
    if (!firstRunCoach?.isActive()) return;
    firstRunCoach.dismiss();
    if (!metaState.tutorialSeen) {
      metaState.tutorialSeen = true;
      void metaStateManager.save(metaState);
    }
  };
  eventBus.on("tower.placed", dismissFirstRunCoach);
  eventBus.on("firewall.placed", dismissFirstRunCoach);
  eventBus.on("wave.started", () => {
    waveIndicator.hide();
    skillDeckBar.update([...draftSystem.cards], draftSystem.maxSlots, gs.wave);
    // BL-232 — the per-wave "GO" beat (EVERY wave, not just wave 1): the path
    // conduits flare to life + the WAVE readout ignites. Render-only; both
    // self-gate reduced motion. The wave.started haptic is already in nativeShell.
    gridRenderer.triggerWaveSurge();
    hud.igniteWave();
  });

  // BL-241 — combo MILESTONE callout (render/UI + audio only). CombatFeel emits
  // `combo.milestone` the first time a chain reaches ×5/×7/×10; present it as a
  // combo-meter flourish + a flash + a TV praise line (D-331: a milestone is
  // praise, not a warning — it moved off the grid). The flash self-gates
  // reduced motion + the FlashGovernor.
  eventBus.on("combo.milestone", (d) => {
    const mult = d.mult;
    const key =
      mult >= 10
        ? "combo.milestone.10"
        : mult >= 7
        ? "combo.milestone.7"
        : "combo.milestone.5";
    comboReadout.flourish();
    screenEffects.flash(COLORS.AMBER_WARN, milestoneFlashAlpha(mult));
    hud.announceStatus(t(key), COMMS_PRIORITY.MED);
  });
  eventBus.on("wave.completed", () => {
    // Bug 7: do NOT eagerly reveal the wave indicator here (this fires before
    // the draft is dismissed). renderPipeline shows it BUILD-only. The trigger
    // was registered at startup (see waveIndicator.show above).
    skillDeckBar.update([...draftSystem.cards], draftSystem.maxSlots, gs.wave);
    hud.resetFastForward();
    // BL-205 — a placed tower's upgrade/sell panel must not persist across the
    // wave boundary into the post-wave / draft window. Clear the selection so
    // the per-frame render loop stops re-deriving it, then tear the panel down.
    setSelectedTower(gs, null);
    gameCtx.towerSelector.hideTowerInfo();
    // BL-210 (S189) — wave-clear CELEBRATION (render/UI + audio only). The base
    // beat (PROCESS CLEAR banner + green flash + particles) is emitted by the
    // wave handler; this layers the premium tension→release on top:
    //  - a brighter celebratory flash + a small punch — both arenaMotionScale-
    //    damped, so silent under reduced motion (and FlashGovernor-rate-capped);
    //  - a green edge sweep (vignette), gated OFF under reduced motion;
    //  - a combo-meter flourish on the board;
    //  - a rising chime stacked over the existing wave_complete chord
    //    (achievement_unlock = that chord + a rising top octave).
    screenEffects.flash(...FEEL_BEATS.waveClear.flash);
    screenEffects.shake(...FEEL_BEATS.waveClear.shake);
    if (!isReducedMotion()) {
      screenEffects.vignette(...FEEL_BEATS.waveClear.vignette);
    }
    comboReadout.flourish();
    audioEngine.playSfx("achievement_unlock");
  });

  let musicDirectorRef: MusicDirector | null = null;
  // S195/BL-283 — the near-loss HEARTBEAT, one owner (audit beat #9). The old
  // mainframe.hit latch is retired: the director polls gs.lives per render
  // frame (render-only read), so the band ESCALATES 3→2→1 (deeper tint,
  // faster heartbeat, music pressure) and — the S195-F14 fix — DISARMS the
  // frame an addLives rescue lifts the integrity back above the band
  // ("lives never auto-heal" was false: the addLives card heals silently).
  // musicDirector is constructed later in boot (after the audio manifest);
  // the slice indirection late-binds it (the firstRunCoach pattern).
  const nearLoss = new NearLossDirector({
    getLives: () => gs.lives,
    screenEffects,
    audio: audioEngine,
    music: { setPressure: (v) => musicDirectorRef?.setPressure(v) },
  });
  const clearNearLoss = (): void => nearLoss.reset();
  eventBus.on("game.over", clearNearLoss);
  eventBus.on("game.won", clearNearLoss);
  eventBus.on("game.restart", clearNearLoss);
  eventBus.on("game.returnToMenu", clearNearLoss);

  // BL-205 — the draft overlay opens on `draft.cardsDrawn` (the wave-complete →
  // draft transition). Belt-and-braces with wave.completed above: force the
  // panel closed so it can never sit on top of the draft cards even if it was
  // re-selected in the gap before the cards are dealt.
  eventBus.on("draft.cardsDrawn", () => {
    setSelectedTower(gs, null);
    gameCtx.towerSelector.hideTowerInfo();
  });

  eventBus.on("game.won", (ev) => {
    // BL-141 — same teardown as game.over: the victory screen is an overlay, but
    // the tower info panel sits above it (z-9999 in #game-container), so clear
    // the selection and dismiss the panel before the screen shows.
    setSelectedTower(gs, null);
    gameCtx.towerSelector.hideTowerInfo();
    victoryScreen.show(
      ev.score,
      ev.wave,
      ev.livesBonus,
      () => {
        finalizeFromVictory?.(ev.wave, ev.livesBonus, true);
        eventBus.emit("game.returnToMenu");
      },
      () => {
        finalizeFromVictory?.(ev.wave, ev.livesBonus, true);
        eventBus.emit("game.restart");
      },
      () => eventBus.emit("game.continueEndless")
    );
  });

  eventBus.on("game.continueEndless", () => {
    // Runtime/score/UI side of CONTINUE only. Wave progression (breach + draft
    // + auto pre-wave countdown) is driven by the shared post-wave path in
    // waveHandlers' own game.continueEndless subscription, so endless wave 36+
    // matches a normal wave transition instead of a bare manual start.
    gs.isEndlessMode = true;
    scoreSystem.freeze();
    victoryScreen.hide();
    hud.update(gs);
  });

  // ─── Start Menu Flow ───────────────────────
  // BL-85: the storage adapter + meta state were loaded early (the BL-115 locale
  // keystone, above) so the persisted locale could seed the UI before it built;
  // the leaderboard + the save-status banner are wired here on the same handles.
  const leaderboardApiBase =
    (import.meta.env.VITE_LEADERBOARD_API as string | undefined)?.trim() ?? "";
  const leaderboardClient = leaderboardApiBase
    ? new HttpLeaderboardClient({ baseUrl: leaderboardApiBase })
    : new NullLeaderboardClient();
  const leaderboardService = new LeaderboardService({
    client: leaderboardClient,
    storage: storageAdapter,
    enabled: leaderboardApiBase !== "",
  });
  // BL-85: belt-and-braces — a storage/init throw beneath the service must
  // never abort boot. The adapter already degrades, but guard here too so a
  // future init path can't white-screen the game.
  try {
    await leaderboardService.init();
    // BL-261: flushing the retry queue does per-item network I/O (~2.5s timeout
    // each). Awaiting it here delayed the boot screen by ~queue-length × 2.5s on
    // a hung network. Fire-and-forget instead — a queued score is not
    // boot-critical; it flushes in the background and re-queues on failure.
    void leaderboardService.flushQueue().catch((e) => {
      console.warn("[boot] leaderboard queue flush failed — will retry:", e);
    });
  } catch (e) {
    console.warn("[boot] leaderboard init failed — continuing:", e);
  }

  // Surface the early meta-load outcome now that the comms channel exists
  // (the load itself happened above, before the UI built — see the BL-115
  // locale block). D-331: this is boot-time STATUS chrome, not a warning the
  // player must react to — it lands on the TV, LOW priority (never starves a
  // real gameplay beat).
  if (metaLoadStatus === "reset") {
    hud.announceStatus("SAVE DATA RESET — STARTING FRESH", COMMS_PRIORITY.LOW);
  } else if (metaLoadStatus === "migrated") {
    hud.announceStatus("SAVE DATA MIGRATED", COMMS_PRIORITY.LOW);
  } else if (metaLoadStatus === "recovered") {
    // BL-86: a current-version save with garbage/missing fields was repaired
    // from defaults rather than crashing boot.
    hud.announceStatus("SAVE DATA RECOVERED", COMMS_PRIORITY.LOW);
  }

  // Apply persisted audio settings to the split buses (PT-19). BL-86: these
  // settings.* derefs stay inside the guarded block — metaState is always a
  // complete normalized MetaState now, so they can't throw, but keep the net.
  try {
    audioEngine.setSfxMuted(metaState.settings.sfxMuted);
    audioEngine.setMusicMuted(metaState.settings.musicMuted);
    audioEngine.setSfxVolume(metaState.settings.sfxVolume);
    audioEngine.setMusicVolume(metaState.settings.musicVolume);
  } catch (e) {
    console.warn("[boot] applying audio settings failed — continuing:", e);
  }
  // BL-110 — install the shared :focus-visible ring before any menu/overlay
  // renders (the dark theme's one deliberate, high-contrast focus indicator).
  ensureA11yStyles();
  // BL-109 — apply the persisted text-size BEFORE any menu UI renders (no flash;
  // index.html ships `--ui-scale:1` statically as the pre-JS default). Drives
  // both the DOM `--ui-scale` var and the canvas `getUiScale()` (one knob).
  // Adaptive stage v2 (mechanism 3b): floor it by the device legibility floor —
  // a SAFE seam. computeUiScaleFloor never demands more than the tuned L layout
  // scale, so max(stored, floor) can't enlarge text past its tuned size (no
  // panel overflow); it only guarantees a sane lower bound on tiny phone stages.
  // Reads the live stage scale published by the index.html fit() (the getStageFit
  // fallback is identity 1 under test → max(L, 0) === L, a no-op).
  try {
    const storedUiScale = metaState.settings.uiScale;
    const legibilityFloor = computeUiScaleFloor(getStageFit().scale);
    setUiScale(Math.max(storedUiScale, legibilityFloor));
  } catch (e) {
    console.warn("[boot] applying text-size failed — continuing:", e);
  }
  // Phase 2a (the decouple): on the phone tier, drive the DOM-ONLY chrome boost
  // from the live stage scale so the HUD/rails/overlays are legible without
  // touching on-board canvas text (which rides the separate getUiScale knob). A
  // no-op on tablet/desktop (computeChromeScale → 1). Re-applied on every fit
  // event the inline index.html script listens to, so an orientation change /
  // iOS toolbar collapse keeps the boost in sync with the published __stageFit.
  const applyChromeScale = (): void => {
    try {
      const fit = getStageFit();
      setChromeScale(computeChromeScale(fit.scale, fit.phoneTier));
    } catch (e) {
      console.warn("[boot] applying chrome-scale failed — continuing:", e);
    }
  };
  applyChromeScale();
  window.addEventListener("resize", applyChromeScale);
  window.addEventListener("orientationchange", applyChromeScale);
  window.visualViewport?.addEventListener("resize", applyChromeScale);
  gameCtx.metaState = metaState;
  // BL-63 — wire the score-race target: the live global #1 (via fetchTop(1),
  // null when disabled / empty / failed → graceful local-best fallback). The
  // fetch is post-/pre-run async UI — determinism-neutral.
  hud.attachScoreRace({
    fetchTopScore: async () => {
      if (!leaderboardService.isEnabled()) return null;
      // Same population as the run being raced. Omitting the level is NOT
      // neutral: `HttpLeaderboardClient.fetchTop` drops the `&threat=` query
      // and the server then answers for `containment` by rule
      // (`functions/_shared/validate.ts` → `resolveThreatLevel`). A fallout run
      // would have raced the containment world #1 — the exact cross-population
      // comparison the 2026-08-16 separation decision forbids. `getLocalBest`
      // below was filtered in the same round; this call was missed.
      const top = await leaderboardService.fetchTop(
        1,
        false,
        metaState.settings.threatLevel
      );
      const first = top[0];
      return first ? { score: first.score, initials: first.initials } : null;
    },
    // The race target must come from the SAME population the run will be
    // ranked in — the player's best at THIS threat level. A flat
    // `highscores[0]` would race a sandbox run against a fallout personal best
    // (owner, 2026-08-16: separation, no normalisation).
    getLocalBest: () =>
      metaState.highscores
        .filter((h) => h.threatLevel === metaState.settings.threatLevel)
        .reduce((best, h) => Math.max(best, h.score), 0),
  });
  eventBus.on("audio.sfxMuteChanged", (muted: unknown) => {
    metaState.settings.sfxMuted = muted as boolean;
    void metaStateManager.save(metaState);
  });
  eventBus.on("audio.musicMuteChanged", (muted: unknown) => {
    metaState.settings.musicMuted = muted as boolean;
    void metaStateManager.save(metaState);
  });
  eventBus.on("audio.setSfxVolume", (vol: unknown) => {
    metaState.settings.sfxVolume = vol as number;
    void metaStateManager.save(metaState);
  });
  eventBus.on("audio.setMusicVolume", (vol: unknown) => {
    metaState.settings.musicVolume = vol as number;
    void metaStateManager.save(metaState);
  });

  // Task-001 (2026-ingame-music-rework, AC-3/AC-6) — register the "run" track
  // as a procedural (composer-driven) source, next to the MusicDirector
  // construction below (composition-root wiring). `getLoadLevel` injects the
  // PerfGovernor pull (B-6 — no governor API change, no new actuator; the
  // composer sheds its arp/percussion layers under sustained load exactly
  // like the arena's own post-FX rungs). MusicDirector itself does not yet
  // route any scene to "run" (that wiring is a later task) — registering it
  // here only makes the source PLAYABLE through the existing engine API.
  audioEngine.registerProceduralMusic("run", {
    getLoadLevel: () => perfGovernor.level,
  });

  // Music policy layer (PT-19): decides which track plays in which scene and
  // when to fade. Menu/deploy/combat edges are driven by direct calls below;
  // run-end edges (game.over/returnToMenu/restart) via its own EventBus subs.
  const musicDirector = new MusicDirector(audioEngine, eventBus);
  musicDirectorRef = musicDirector;

  let achievementTracker: AchievementTracker | null = null;
  let lastSelection: PreRunSelection | null = null;
  // iOS native boot/title ambience bridge (null off-device → Web-Audio path).
  // Assigned just before the boot screen shows; see src/platform/nativeAudio.ts.
  let nativeBootAudio: BootAudioBridge | null = null;
  // WOW Pass 2 (item 1) — Apple Game Center bridge (the iOS GLOBAL leaderboard;
  // null off-device → web/local path). Assigned just before the boot screen
  // shows; see src/platform/gameCenter.ts. DCE'd from the web bundle.
  let nativeGameCenter: GameCenterBridge | null = null;
  let runCounter = 0;
  let finalizeFromVictory:
    | ((wave: number, livesBonus?: number, won?: boolean) => void)
    | null = null;

  function startRun(selection: PreRunSelection): void {
    if (__NATIVE_SHELL__) crumb("board:startRun");
    // G1: DEPLOY always starts a pristine run — resetForNewRun at the head of
    // every run-start path (first boot, return-to-menu, restart). Called EXACTLY
    // ONCE per run-start here; removed from the event handlers to avoid double-wire.
    resetForNewRun(
      gameCtx,
      gameLoop,
      grid,
      recalcAndSyncBreach,
      pauseOverlay,
      triggerWaveStart
    );
    // BL-212 fix — re-arm the first-run coach from idle each run, so a player
    // who advanced to "TAP TO PLACE" then restarted without placing doesn't see
    // a stale step-② nudge on the fresh run (start() below re-shows step ①).
    firstRunCoach?.dismiss();
    const runSeed = (sessionSeed + runCounter) | 0;
    runCounter++;
    // 2026-08-11 (owner: "neues seed… neues grid… bei restart") — the BOARD is
    // re-rolled per run, not just the spawns. `resetSpawnLayout` was already
    // per-run, so spawn positions did vary across restarts; the procedural
    // blockers did not. They were placed once in `createGrid` at boot and then
    // stood for the whole session, which is what made a restarted run read as
    // the same map with the same cover behind the same walls.
    //
    // Blockers FIRST: spawn eligibility is path-validated against them
    // (`collectEligibleSpawnCandidates`), so this order judges the new spawns
    // against the new cover instead of the previous run's.
    gridSetup.regenerateBlockers(runSeed);
    gridSetup.resetSpawnLayout(runSeed);
    breachSystem.init(gridSetup.getSpawnLayout());
    spawnDetectionSystem.init(gridSetup.getSpawnLayout());
    recalcAndSyncBreach();
    lastSelection = selection;
    // Task-002 (2026-threat-profile-model, AC-8/AC-9) — the run-start "Naht":
    // announces that a run began, carrying the persisted threat level so a
    // later balance pass can draw on real selection history (spec §6). This
    // is the ONLY place startRun() is defined, so it fires on every call —
    // the initial deploy AND every restart (the `lastSelection` replay at
    // game.restart, below) — carrying the same level restart replays carry.
    // Process-internal only (AC-15): nothing subscribes this to persistence
    // or the network.
    eventBus.emit("run.started", { threatLevel: selection.threatLevel });
    hud.setOperatorInitials(metaState.playerInitials);
    // BL-63 — resolve the score-race target for this run (local best now, the
    // global #1 async; never blocks).
    hud.beginScoreRace();
    // Chain step A (2026-08-18): the run's BASE difficulty first — the threat
    // profile (enemy HP/speed axes) lands on the WaveManager BEFORE any
    // operator/module augment touches a system. Containment is the 1.00
    // anchor; a restart replays `lastSelection` through this same line, so
    // the profile always matches the level being played.
    applyThreatLevelToSystems(selection.threatLevel, { waveManager });
    applyOperatorToSystems(selection.operator, {
      gs,
      combatSystem,
      economySystem: economy,
      synergySystem,
    });
    // S126: fresh corruption clock + the operator identity color ECHELON will
    // consume (render-only; reads the selection, never writes anything back).
    corruption.beginRun(selection.operator);
    applyModulesToSystems(selection.modules, {
      gs,
      combatSystem,
      draftSystem,
      scoreSystem,
      economySystem: economy,
      synergySystem,
    });
    // Snapshot module baselines for card effect removal
    gameCtx.moduleBaselines = {
      rangeMultiplier: combatSystem.rangeMultiplier,
      bossDamageReduction: combatSystem.bossDamageReduction,
      globalDamageMult: combatSystem.globalDamageMult,
    };
    skillOverview.maxSlots = draftSystem.maxSlots;
    skillDeckBar.update([...draftSystem.cards], draftSystem.maxSlots, gs.wave);
    gameCtx.failsafeAvailable = selection.modules.includes("failsafe_protocol");
    // `adaptive_throttling` — the catalogue's last unwired promise ("Quality
    // reduces at 14ms", both locales). Resolved from the COMMITTED selection
    // and set per run; `resetForNewRun` (above) has already restored the 16 ms
    // default, so a run without the module cannot inherit 14 from the last one.
    perfGovernor.setFrameBudgetMs(getAutoDegradeThreshold(selection.modules));
    achievementTracker = new AchievementTracker(eventBus);
    hud.enableFastForward(gameLoop);

    let runEndHandled = false;
    const runEndUnsubs: Array<() => void> = [];

    const finalizeRunEnd = (
      wave: number,
      presetLivesBonus?: number,
      won = false
    ): void => {
      if (runEndHandled || !achievementTracker) return;
      runEndHandled = true;
      const livesBonus =
        presetLivesBonus ??
        scoreSystem.addLivesBonus(gs.lives, GAME.INITIAL_HP);
      const stats = {
        score: scoreSystem.score,
        wave,
        operator: selection.operator,
        maxCombo: scoreSystem.maxCombo,
        leaks: achievementTracker.getStats().leaks,
        livesAtEnd: gs.lives,
        livesBonus,
        won,
        // The score's comparison key — the level this run was actually played
        // on, straight from the committed selection (owner, 2026-08-16).
        threatLevel: selection.threatLevel,
      };
      // WOW Pass 2 (item 2) — snapshot the unlocked set BEFORE the handler so we
      // can detect a freshly-unlocked operator afterwards (read-only; the unlock
      // push lives in runEndHandler, thresholds/logic untouched).
      const unlockedBefore = new Set(metaState.unlockedOperators);
      handleRunEnd(
        stats,
        achievementTracker,
        metaState,
        metaStateManager,
        eventBus,
        leaderboardService
      )
        .then(() => {
          achievementTracker?.destroy();
          achievementTracker = null;
          // A new AI came online this run → celebrate it over the run-end screen.
          const newlyUnlocked = metaState.unlockedOperators.filter(
            (op) => !unlockedBefore.has(op)
          );
          if (newlyUnlocked.length > 0) {
            operatorUnlockBanner.show(
              newlyUnlocked.map((op) => OPERATOR_NAMES[op])
            );
          }
          // WOW Pass 2 (item 1) — submit the run score to Game Center on iOS
          // (native ONLY). The web custom-HTTP submit stays inside handleRunEnd
          // and is NOT extended to iOS (Apple owns the iOS global path). Web
          // build DCEs this via the __NATIVE_SHELL__ literal.
          //
          // `!__GC_DIAG__`: the GC DEVICE-GATE build is READ-ONLY by owner
          // directive (2026-08-17 — no test submit without explicit release,
          // and a submit would contaminate the very score-state reading the
          // gate exists to take). The literal compiles this whole branch OUT of
          // the gate binary, so finishing a run on the gate build cannot write
          // a Game Center score. Product builds (__GC_DIAG__ = false) keep the
          // submit path unchanged.
          if (__NATIVE_SHELL__ && !__GC_DIAG__ && nativeGameCenter) {
            // The run's OWN level — the population it was played in, never the
            // one currently selected in the menu (a player can change the
            // selector between finishing a run and the submit landing).
            nativeGameCenter.submitScore(stats.score, stats.threatLevel);
          }
        })
        .catch(() => {
          /* graceful */
        });

      for (const unsub of runEndUnsubs) unsub();
      runEndUnsubs.length = 0;
      if (finalizeFromVictory === finalizeRunEnd) {
        finalizeFromVictory = null;
      }
    };

    finalizeFromVictory = finalizeRunEnd;

    runEndUnsubs.push(
      eventBus.on("game.over", () => {
        // BL-141 — clear the selected tower so the 2 trailing render frames
        // (gameLoop.stopAfterFrames(2)) don't re-paint the z-9999 info panel
        // ON TOP of the game-over screen; tear down any panel already mounted.
        setSelectedTower(gs, null);
        gameCtx.towerSelector.hideTowerInfo();
        // Bug 16: in an endless run seeded by a wave-35 win, carry the stored
        // campaign integrity bonus into the finalize (the live score is frozen,
        // so addLivesBonus would report 0). 0 for a plain non-endless death.
        // S130: an endless death still counts as a WON campaign (Echo Mode is
        // victory's epilogue — Story Bible §4).
        finalizeRunEnd(
          gs.wave,
          gs.campaignLivesBonus || undefined,
          gs.isEndlessMode
        );
      })
    );
    runEndUnsubs.push(
      eventBus.on("game.returnToMenu", () => {
        if (!runEndHandled && finalizeFromVictory === finalizeRunEnd) {
          finalizeFromVictory = null;
        }
        for (const unsub of runEndUnsubs) unsub();
        runEndUnsubs.length = 0;
      })
    );
    runEndUnsubs.push(
      eventBus.on("game.restart", () => {
        if (!runEndHandled && finalizeFromVictory === finalizeRunEnd) {
          finalizeFromVictory = null;
        }
        for (const unsub of runEndUnsubs) unsub();
        runEndUnsubs.length = 0;
      })
    );

    setWaveCountdown(gs, 0); // Wave 1: no auto-countdown, player must manually start
    setWaveStartedThisCountdown(gs, false);
    if (__NATIVE_SHELL__) crumb("board:loop-start");
    gameLoop.start();
    // The "drop": combat music restarts fresh on the other side of the deploy
    // cut. S143 / BL-129(b): use the french-house sweep variant here — the
    // longer brightness/width riser that turns this seam (the one entering-
    // the-battle moment) into the rising sweep the owner asked for. Restart
    // and programmatic flows keep the plain `enterCombat()` (no surprise on
    // mid-run state transitions).
    musicDirector.enterCombatSweep();
    // BL-190 — the composed board POWER-ON beat: a one-time scan-in + chrome
    // glint as the play surface lights up, landing inside the music riser above.
    // Render-only (DOM/CSS + the reduced-motion-gated chrome glint), one-shot
    // (self-removes), reduced-motion-safe (no-op). The one absent hero moment
    // (UI_AUDIT §A/§3); adds zero steady-state peak-play density.
    playBoardEntrance(document.getElementById("main-area"), {
      chrome: uiChrome,
      glintPanels: [
        document.getElementById("left-panel"),
        document.getElementById("right-panel"),
      ].filter((el): el is HTMLElement => el != null),
    });
    if (__NATIVE_SHELL__) {
      crumb("board:entrance");
      crumbFrames("board");
    }
    // BL-215 fix — the board overlay runs only while a run is live (kept off the
    // boot / start / pre-run phases that loop on a memory-tight iPhone).
    comboReadout.start();
    // BL-212 (S189) — first-run micro-coach: the very first time the board lights
    // up, nudge the brand-new player (TAP A TOWER → TAP TO PLACE). Never again
    // after — tutorialSeen is persisted on the first placement (below).
    if (!metaState.tutorialSeen) firstRunCoach?.start();
  }

  function showMainMenu(): void {
    if (__NATIVE_SHELL__) crumb("menu:show");
    musicDirector.enterMenu();
    preRunScreen.show(
      metaState,
      startRun,
      () => {
        // BACK → the settled TITLE screen (device round 2026-08-18: no POST
        // replay). The title theme restarts via skipToIdle's onTitleReveal.
        musicDirector.enterBoot();
        void showBootSequence(true);
      },
      () => musicDirector.onDeploy(),
      async () => {
        // RESET PROGRESS (confirmed in-screen): wipe the persisted save, drop
        // back to a pristine default, and re-render the menu so the lock bars
        // and unlocked operators reflect the fresh state.
        await metaStateManager.reset();
        metaState = metaStateManager.getDefault();
        showMainMenu();
      },
      // BL-115 — the EN/DE toggle: seed the runtime locale, mutate + persist the
      // setting (via MetaStateManager, never raw localStorage), and let PreRun
      // re-render itself in the new language. `i18n.currentLocale` is the runtime
      // truth; `settings.language` its persisted seed — both updated here.
      (lang) => {
        metaState.settings.language = lang;
        setLocale(lang);
        void metaStateManager.save(metaState);
      }
    );
  }

  async function showBootSequence(startAtTitle = false): Promise<void> {
    // S191 Defect A — one crumb per ENTRY into the boot flow. Unlike `boot:start`
    // (once per JS context), this fires every time showBootSequence runs, so a
    // same-context re-entry loop (e.g. menu BACK, or a logic bug) shows repeated
    // `boot:enter` under a SINGLE `boot:start`; a native reload shows each
    // `boot:enter` under its OWN climbing `boot:start`/`boot:ctx`.
    if (__NATIVE_SHELL__) crumb("boot:enter");
    bootSequence.show(() => showMainMenu(), {
      // ── ONE POPULATION PER BOARD (owner, 2026-08-16) ─────────────────────
      // The board shows the level the player is currently set to — the same
      // `settings.threatLevel` the pre-run screen opens on. Mixing levels
      // would put a sandbox score (0.85x enemy HP) above a fallout score with
      // nothing telling them apart, which is exactly what the threat contract
      // separates. This is a FILTER, not a design decision.
      //
      // The design decision it exposes — should the board TELL the player
      // which population it is showing, and can they switch? — was DECIDED by
      // the owner on 2026-08-17 and is implemented below: the board carries a
      // `‹ THREAT LEVEL ›` switcher and every source answers for it.
      localHighscores: metaState.highscores.filter(
        (h) => h.threatLevel === metaState.settings.threatLevel
      ),
      // Initial view = the level the player last selected, which is also the
      // level of their last run (the run reads the same setting).
      initialBoardLevel: metaState.settings.threatLevel,
      localHighscoresFor: (level) =>
        metaState.highscores.filter((h) => h.threatLevel === level),
      // WOW Pass 2 (item 1) — on iOS the GLOBAL board is Game Center: enable the
      // board when a GC bridge exists and feed it GC loadTop (mapped to the same
      // RankedEntry rows the premium board already renders, keeping our look over
      // the native VC). Off-device the __NATIVE_SHELL__ literal DCEs this back to
      // the web custom-HTTP path unchanged. A signed-out GC → loadTop [] → the
      // board's graceful "GLOBAL — OFFLINE" degrade (never blocks the app).
      enabled:
        leaderboardService.isEnabled() ||
        (__NATIVE_SHELL__ && nativeGameCenter !== null),
      // BL-95: includeSelf=true → the server flags our own rows via `self`
      // (no stable id echoed) so the boot board can highlight "you". On GC the
      // local player's own row carries the same `self` flag (isLocalPlayer).
      fetchGlobal:
        __NATIVE_SHELL__ && nativeGameCenter
          ? () => nativeGameCenter!.loadTop(10, metaState.settings.threatLevel)
          : () =>
              leaderboardService.fetchTop(
                10,
                true,
                metaState.settings.threatLevel
              ),
      // WOW Pass 2 (item 1) — GC present but signed-out → the board shows the
      // "SIGN IN TO COMPETE" CTA + local best (compete front door, never blocks),
      // and a VIEW GLOBAL RANKS button that pops the native GC board. Off-device
      // the __NATIVE_SHELL__ literal DCEs both back to the web path (no CTA).
      globalSignedOut:
        __NATIVE_SHELL__ &&
        nativeGameCenter !== null &&
        !nativeGameCenter.authenticated,
      onViewGlobalRanks:
        __NATIVE_SHELL__ && nativeGameCenter
          ? () =>
              nativeGameCenter!.showNativeLeaderboard(
                metaState.settings.threatLevel
              )
          : undefined,
      // BL-130 — the boot-screen ⚙ gear bridge (language · audio · legal). The
      // same persist+apply hooks the PreRun gear uses. S146/BL-142: the text-size
      // control is retired (the app is L-only), so no uiScale hook here.
      settings: {
        language: metaState.settings.language,
        bus: eventBus,
        onSetLanguage: (lang) => {
          metaState.settings.language = lang;
          setLocale(lang);
          void metaStateManager.save(metaState);
        },
      },
      // BL-143 (S147) — the boot ambience cues (BULLYFINGER grit + sting
      // samples). The music director is silent across the boot screen, so
      // these own the audio until the title reveal raises the theme. On iOS
      // the native bridge (nativeBootAudio) plays them immediately at launch
      // (no Web-Audio gesture gate); off-device it's null and the Web-Audio
      // AudioEngine path is used. Task-001 (2026-audio-polish, AC-2) — the
      // third cue this bridge used to carry (title floppy loop) is retired;
      // the theme now owns the whole title→menu→deploy arc via the SEPARATE
      // `onTitleReveal` hook below.
      audio: nativeBootAudio ?? {
        loadingAmbienceIn: () => audioEngine.playBootGrit(),
        loadingAmbienceOut: () => audioEngine.stopBootGrit(),
        threatSting: () => audioEngine.playThreatSting(),
      },
      // Task-001 (2026-audio-polish, AC-3) — the title reveal: raise the
      // theme (LPF+phaser intro armed, engine-side) via the director, next
      // to the ambience bridge above. Runs on BOTH web and iOS (the director/
      // engine pick the right transport internally) — no `__NATIVE_SHELL__`
      // branch needed here.
      onTitleReveal: () => musicDirector.enterTitle(),
      startAtTitle,
    });
    // WOW Pass 2 (item 1) — sign-in no longer blocks boot, so the board first
    // paints with the "SIGN IN TO COMPETE" CTA; when Game Center authenticates
    // (silently from cache, after the first-launch sheet, or via the native
    // board) flip the GLOBAL pane to the live rows in place — no relaunch. DCE'd
    // on web (no native bridge). Fires immediately if already authenticated.
    if (__NATIVE_SHELL__ && nativeGameCenter) {
      nativeGameCenter.onAuthenticated(() => {
        bootSequence.notifyGlobalAuthenticated();
        // Identity seam (owner decision 2026-08-18): the Game Center alias
        // seeds the Operator callsign ONCE, while it is still the default —
        // a first-run player lands in PreRun with their own name already on
        // the console instead of "AAA". A callsign the player chose is never
        // overwritten (policy in core/initials.ts, prefillInitials).
        const alias = nativeGameCenter?.localAlias;
        const seeded = alias
          ? prefillInitials(metaState.playerInitials, alias)
          : null;
        if (seeded) {
          metaState.playerInitials = seeded;
          void metaStateManager.save(metaState);
        }
      });
    }
    // BL-229 — frame-probe the boot render itself (raf1/raf2/+500ms/+1500ms/
    // +3000ms-survived), the heaviest cold first-launch moment. Until now only
    // PreRun + the board were probed, so a reap DURING the boot screen left just
    // [boot:start, boot:start] — which showDiagnosticOverlay SUPPRESSES — making
    // the post-update boot reap invisible (why the owner never saw a crash trail
    // despite the loop). Now the NEXT launch can paint exactly how far the boot
    // render got before it died, so the reap is proven, not inferred.
    crumbFrames("boot");
  }

  eventBus.on("game.returnToMenu", () => {
    // G1: full reset moved to startRun head. Stop the loop + hide overlays here
    // so the pre-run screen appears on a quiet stage. The pristine init runs
    // once inside startRun when the player hits DEPLOY.
    gameLoop.stop();
    gameCtx.gameOverScreen.hide();
    gameCtx.victoryScreen?.hide();
    if (gameCtx.draftUI.visible) gameCtx.draftUI.hide();
    if (gameCtx.skillOverview.visible) gameCtx.skillOverview.hide();
    gameCtx.spawnAlert.hide();
    // BL-141 — the tower upgrade/sell panel (a z-9999 fixture in #game-container,
    // not an overlay layer) was never torn down here, so it persisted into the
    // menu. The loop is already stopped, so a direct teardown is permanent.
    gameCtx.towerSelector.hideTowerInfo();
    pauseOverlay.hide();
    showMainMenu();
  });

  eventBus.on("game.restart", () => {
    if (!lastSelection) return;
    // G1: startRun calls resetForNewRun at its head (stops loop, full pristine init).
    startRun(lastSelection);
  });

  // Mount inside the fixed-stage container (not <body>) so the boot screen
  // scales as part of the same 1600×900 composition. Its position:absolute
  // (S183/BL-201) fills the stage frame exactly, so it covers the game and scales
  // with it — the same frame envelope every other screen uses.
  // Native shell: hide the launch splash the instant the boot screen is ready,
  // so the splash → boot-sequence handoff shows no blank frame (web build: DCE'd).
  if (__NATIVE_SHELL__) {
    crumb("boot:splash-hide");
    void import("./platform/nativeShell.ts").then((shell) =>
      shell.hideNativeSplash()
    );
  }

  const bootSequence = new BootSequence(requireElement("game-container"));

  // iOS/iPad: route the boot/title ambience through NATIVE audio so it sounds
  // immediately at launch — Web Audio is gesture-gated on iOS and the boot/title
  // screens run before the first tap, so their cues would otherwise be silent
  // (see src/platform/nativeAudio.ts). Awaited so the cues are preloaded before
  // the boot screen shows; null off-device (or if preload fails) → showBootSequence
  // keeps the Web-Audio path. Web build DCEs this branch + the dynamic import.
  if (__NATIVE_SHELL__) {
    nativeBootAudio = await import("./platform/nativeAudio.ts")
      .then((m) => m.createNativeBootAudio())
      .catch(() => null);
    // WOW Pass 2 (item 1) — authenticate Game Center at launch (GC shows its own
    // sign-in sheet) so the boot board can surface the player's GLOBAL STANDING.
    // Awaited like the boot audio so the bridge is ready when showBootSequence
    // runs; best-effort (a non-authenticated/declined bridge still returns, and
    // the board degrades to "SIGN IN TO COMPETE" + local best). Web build DCEs
    // this branch + the dynamic import entirely.
    //
    // Gated by __FEATURE_GAME_CENTER__ — ON for every capacitor build since
    // 2026-08-18 (vite.config: mode === "capacitor"): the five ASC boards
    // exist, the distribution profile carries the Game Center capability, and
    // auth was proven on device (TestFlight build 104; see the SDD spec's
    // gate-game-center-device.md). On web the false literal still DCEs the
    // whole GC path — the dynamic import, the "GameCenter" plugin bridge, and
    // the Swift GameKit calls it fronts — and nativeGameCenter stays null, so
    // every downstream GC branch degrades to the web/local leaderboard path.
    if (__FEATURE_GAME_CENTER__) {
      nativeGameCenter = await import("./platform/gameCenter.ts")
        .then((m) => m.createNativeGameCenter(storageAdapter))
        .catch(() => null);
    }
    // GC DEVICE GATE (2026-08-17): mount the read-only diagnostic overlay —
    // ALSO when the bridge came back null, because "bridge is null on device"
    // is itself a gate finding that must be readable, not hidden. Product
    // builds compile this out (__GC_DIAG__ = false ⇒ DCE, module never loaded).
    if (__GC_DIAG__) {
      void import("./platform/gcDiagOverlay.ts")
        .then((m) => m.mountGcDiagOverlay(nativeGameCenter))
        .catch(() => {});
    }
    // Revision 2026-07-22 ("Ton jetzt retten, Effekt später") — route the
    // pre-game THEME through the PROVEN community `native-audio` transport
    // (`createThemeAudio`, restored). The Build-67 AudioKit ThemeAudio plugin
    // shipped SILENT on device; the theme is back on a transport known to
    // sound, the native phaser/LPF intro deferred until device-verifiable.
    // The wiring is unchanged (same `createThemeAudio`/`setThemeAudio` slot):
    // AudioEngine delegates the theme's start/stop/volume to it when
    // present; off-device / on failure it stays on the Web-Audio path.
    // BL-228 — but load it LAZILY (deferred off the boot path): the theme
    // buffer is only needed at pre-run/menu, so keeping it out of the heavy
    // COLD first-launch render shrinks the post-update jetsam memory peak
    // (the first-launch boot-loop). setThemeAudio() auto-starts the theme
    // (playThemeIntro) if it was already requested by the time it loads.
    setTimeout(() => {
      void import("./platform/nativeAudio.ts")
        .then((m) => m.createThemeAudio())
        .then((theme) => {
          if (theme) {
            audioEngine.setThemeAudio(theme);
          } else {
            // Task-001 (AC-1 / H3) — the theme's native preload failed (or
            // the plugin is otherwise unavailable): the Web-Audio path
            // stays authoritative. crumb()'s own preload-outcome line (see
            // nativeAudio.ts createThemeAudio) records WHY; this records the
            // resulting fallback decision.
            crumb("audio:native.fallback web-audio-path stays authoritative");
          }
        })
        .catch(() => {});
    }, 800);
  }

  // Native: if the previous session left a crash trail, show it first and wait
  // for the owner to tap CONTINUE before booting (so the boot-loop pauses and
  // the trail is readable / screenshot-able). Web build DCEs this whole branch.
  if (
    __NATIVE_SHELL__ &&
    showDiagnosticOverlay(() => void showBootSequence())
  ) {
    console.log("[AI Wars] diagnostic trail shown — awaiting CONTINUE");
  } else {
    await showBootSequence();
  }

  console.log("[AI Wars] v3.0 — TypeScript Build initialized");
}

boot().catch((err) => {
  // BL-261: build the fallback screen without interpolating the error into
  // innerHTML — the message can carry file paths / HTTP status / validation
  // text, and textContent keeps it inert (no latent injection if an
  // attacker-influenced string ever reaches an error message).
  document.body.replaceChildren();
  const box = document.createElement("div");
  box.style.cssText = `color:${COLORS.MALWARE_RED};font-family:monospace;padding:40px;text-align:center`;
  const h = document.createElement("h2");
  h.textContent = "SYSTEM BOOT FAILURE";
  const p = document.createElement("p");
  p.textContent = String(err);
  box.append(h, p);
  document.body.appendChild(box);
});
